import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PitchAnimation from "./PitchAnimation";

const narratorAnalysisCache = new Map();

class HttpResponseError extends Error {
  constructor(message, status, detail = null) {
    super(message);
    this.name = "HttpResponseError";
    this.status = status;
    this.detail = detail;
  }
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function parseArrayIndex(value) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function clampIndex(value, length) {
  if (!Number.isFinite(length) || length <= 0) return 0;
  return Math.min(Math.max(0, value), length - 1);
}

function nearestIndexOrder(length, requestedIndex) {
  if (!Number.isFinite(length) || length <= 0) return [];

  const start = clampIndex(requestedIndex, length);
  const order = [start];

  for (let distance = 1; order.length < length; distance += 1) {
    const before = start - distance;
    const after = start + distance;

    if (before >= 0) order.push(before);
    if (after < length) order.push(after);
  }

  return order;
}

function parseSubjectDeepLink(href) {
  const url = new URL(href, window.location.origin);
  const parts = url.pathname.split("/").filter(Boolean);
  const subjectPosition = parts.indexOf("subject");

  if (subjectPosition < 0 || !parts[subjectPosition + 1]) {
    throw new Error("The example link is not a valid /subject/... deep link.");
  }

  return {
    subjectId: decodeURIComponent(parts[subjectPosition + 1]),
    requestedPartIndex: parseArrayIndex(parts[subjectPosition + 2]),
    requestedSentenceIndex: parseArrayIndex(parts[subjectPosition + 3]),
  };
}

function resolveBackendUrl(value) {
  if (!value || typeof value !== "string") return null;
  if (/^https?:\/\//i.test(value)) return value;

  const apiBase = String(process.env.REACT_APP_USER_API_URL || "").replace(/\/$/, "");
  return value.startsWith("/") ? `${apiBase}${value}` : `${apiBase}/${value}`;
}

function formatApiDetail(value, fallbackMessage) {
  if (typeof value === "string" && value.trim()) return value;

  if (Array.isArray(value)) {
    const messages = value
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return String(item ?? "");

        const location = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== "body").join(".")
          : "";
        const message = item.msg || item.message || item.detail || "Invalid value";
        return location ? `${location}: ${message}` : String(message);
      })
      .filter(Boolean);

    if (messages.length) return messages.join("; ");
  }

  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (typeof value.detail === "string") return value.detail;

    try {
      return JSON.stringify(value);
    } catch {
      return fallbackMessage;
    }
  }

  return fallbackMessage;
}

async function readError(response, fallbackMessage) {
  try {
    const payload = await response.json();
    const detail = payload?.detail ?? payload?.message ?? payload;
    return {
      message: formatApiDetail(detail, fallbackMessage),
      detail,
    };
  } catch {
    return { message: fallbackMessage, detail: null };
  }
}

async function fetchJson(url, options, fallbackMessage) {
  const response = await fetch(url, options);

  if (!response.ok) {
    const error = await readError(response, fallbackMessage);
    throw new HttpResponseError(error.message, response.status, error.detail);
  }

  return response.json();
}

function getPartFilename(part) {
  if (typeof part === "string") return part;
  if (part && typeof part === "object") {
    return part.file || part.full_path || part.path || "";
  }
  return "";
}

function numericArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => {
    if (value == null) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  });
}

function normalizeSeries(candidate) {
  if (!candidate) return null;

  if (Array.isArray(candidate)) {
    const points = candidate
      .map((point) => {
        if (Array.isArray(point) && point.length >= 2) {
          return { x: Number(point[0]), y: Number(point[1]) };
        }
        if (point && typeof point === "object") {
          return {
            x: Number(point.x ?? point.time ?? point.t ?? point.timestamp),
            y: Number(
              point.y ??
                point.value ??
                point.pitch ??
                point.frequency ??
                point.intensity
            ),
          };
        }
        return null;
      })
      .filter((point) => point && Number.isFinite(point.x));

    if (!points.length) return null;
    return {
      xs: points.map((point) => point.x),
      ys: points.map((point) => (Number.isFinite(point.y) ? point.y : null)),
    };
  }

  if (typeof candidate !== "object") return null;

  const nested = candidate.data || candidate.points || candidate.samples;
  if (nested && nested !== candidate) {
    const normalizedNested = normalizeSeries(nested);
    if (normalizedNested) return normalizedNested;
  }

  const xs =
    candidate.xs ||
    candidate.x ||
    candidate.times ||
    candidate.timestamps ||
    candidate.time;
  const ys =
    candidate.ys ||
    candidate.y ||
    candidate.values ||
    candidate.frequencies ||
    candidate.pitch_values ||
    candidate.intensity_values;

  const normalizedXs = numericArray(xs);
  const normalizedYs = numericArray(ys);
  const count = Math.min(normalizedXs.length, normalizedYs.length);

  if (!count) return null;

  return {
    xs: normalizedXs.slice(0, count),
    ys: normalizedYs.slice(0, count),
  };
}

function firstSeries(candidates) {
  for (const candidate of candidates) {
    const normalized = normalizeSeries(candidate);
    if (normalized?.xs?.length && normalized?.ys?.length) return normalized;
  }
  return null;
}

function extractNarratorVisualization(payload) {
  const narrator = payload?.narrator_intonation || payload?.narrator || {};

  // This is the response shape already handled by NarratorIntonation:
  // narrator_intonation.intensity = {
  //   intensity: { xs, ys },
  //   pitch: { xs, ys },
  //   pitch_1st_derivative: ...
  // }
  //
  // Some older responses instead expose narrator_intonation.pitch and
  // narrator_intonation.intensity directly, so keep both layouts supported.
  const combinedAnalysis =
    narrator?.intensity && typeof narrator.intensity === "object"
      ? narrator.intensity
      : null;

  const hasCombinedAnalysis = Boolean(combinedAnalysis?.intensity);

  const pitch = firstSeries([
    hasCombinedAnalysis ? combinedAnalysis?.pitch : null,
    narrator?.pitch,
    narrator?.pitch_data,
    narrator?.pitchData,
    narrator?.intonation?.pitch,
    narrator?.intonation?.pitch_data,
    narrator?.analysis?.pitch,
    payload?.pitch,
    payload?.pitch_data,
    payload?.analysis?.pitch,
  ]);

  const intensity = firstSeries([
    hasCombinedAnalysis ? combinedAnalysis?.intensity : null,
    hasCombinedAnalysis ? null : narrator?.intensity,
    narrator?.intensity_data,
    narrator?.intensityData,
    narrator?.intonation?.intensity,
    narrator?.intonation?.intensity_data,
    narrator?.analysis?.intensity,
    payload?.intensity,
    payload?.intensity_data,
    payload?.analysis?.intensity,
  ]);

  const fragmentPath =
    narrator?.fragment?.url ||
    narrator?.audio_url ||
    payload?.segment_audio_url ||
    narrator?.file_narrator;

  return { narrator, pitch, intensity, fragmentPath };
}

async function loadSegments(subject, part, signal) {
  const filename = getPartFilename(part);
  if (!filename) throw new Error("The selected audiobook part has no filename.");

  const payload = {
    author: subject.author,
    title: subject.title,
    language: subject.language,
    file: filename,
    type: subject.type,
  };

  const json = await fetchJson(
    `${process.env.REACT_APP_USER_API_URL}/whisper/get-segments`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
      signal,
    },
    "Could not load the example sentence."
  );

  return Array.isArray(json?.segments) ? json.segments : [];
}

async function loadNearestPlayablePart({ subject, parts, requestedPartIndex, signal }) {
  const candidates = nearestIndexOrder(parts.length, requestedPartIndex);
  let lastError = null;

  for (const partIndex of candidates) {
    const file = parts[partIndex];

    try {
      const segments = await loadSegments(subject, file, signal);
      if (segments.length > 0) return { partIndex, file, segments };
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      if (error instanceof HttpResponseError) throw error;
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  throw new Error("No playable sentence was found in this subject.");
}

async function loadNarratorAnalysis({ subject, file, segment, cacheKey, signal }) {
  const cached = narratorAnalysisCache.get(cacheKey);
  if (cached) return cached;

  const filename = getPartFilename(file);
  if (!filename) {
    throw new Error("The narrator fragment cannot be prepared without a filename.");
  }

  const encode = (value) => encodeURIComponent(String(value ?? ""));
  const url =
    `${process.env.REACT_APP_USER_API_URL}/analysis/narrator/` +
    `${encode(subject.owner)}/` +
    `${encode(subject.language)}/` +
    `${encode(subject.author)}/` +
    `${encode(subject.title)}/` +
    `${encode(filename)}/` +
    `${encode(segment.start)}/` +
    `${encode(segment.end)}/` +
    `${encode(subject.type)}`;

  const formData = new FormData();
  formData.append("text", segment?.text || "");

  const payload = await fetchJson(
    url,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData,
      signal,
    },
    "Could not prepare the narrator fragment."
  );

  const analysis = extractNarratorVisualization(payload);

  if (!analysis.fragmentPath) {
    throw new Error("The narrator service returned no playable audio URL.");
  }

  narratorAnalysisCache.set(cacheKey, analysis);
  return analysis;
}

async function makePlayableAudioUrl(fragmentPath, signal) {
  const resolvedUrl = resolveBackendUrl(fragmentPath);
  if (!resolvedUrl) throw new Error("The narrator fragment URL could not be resolved.");

  if (!resolvedUrl.includes("/audio/private/")) {
    return { url: resolvedUrl, objectUrl: false };
  }

  const response = await fetch(resolvedUrl, {
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    const error = await readError(
      response,
      "Could not open the protected narrator fragment."
    );
    throw new HttpResponseError(error.message, response.status, error.detail);
  }

  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), objectUrl: true };
}

export default function SharedSentenceNarratorPlayer({
  href,
  label = "Narrator example",
  compact = false,
}) {
  const audioRef = useRef(null);
  const fallbackAudioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [state, setState] = useState({
    status: "loading",
    error: null,
    audioUrl: null,
    objectUrl: false,
    pitch: null,
    intensity: null,
    segment: null,
    sentenceText: "",
    subjectTitle: "",
    author: "",
    partIndex: 0,
    sentenceIndex: 0,
    canonicalPath: "",
  });

  const requested = useMemo(() => {
    try {
      return { value: parseSubjectDeepLink(href), error: null };
    } catch (error) {
      return { value: null, error };
    }
  }, [href]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let createdObjectUrl = null;

    const load = async () => {
      if (requested.error || !requested.value) {
        setState((previous) => ({
          ...previous,
          status: "error",
          error: requested.error?.message || "Invalid sentence link.",
        }));
        return;
      }

      const { subjectId, requestedPartIndex, requestedSentenceIndex } = requested.value;

      setIsPlaying(false);
      setState((previous) => ({
        ...previous,
        status: "loading",
        error: null,
        audioUrl: null,
        pitch: null,
        intensity: null,
        segment: null,
      }));

      try {
        const subject = await fetchJson(
          `${process.env.REACT_APP_USER_API_URL}/subjects/by-id/${encodeURIComponent(
            subjectId
          )}`,
          {
            method: "GET",
            headers: getAuthHeaders(),
            signal: controller.signal,
          },
          "Could not load the example subject."
        );

        const structure = await fetchJson(
          `${process.env.REACT_APP_USER_API_URL}/subjects/get-structure`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              owner: subject.owner,
              author: subject.author,
              title: subject.title,
              language: subject.language,
              type: subject.type,
            }),
            signal: controller.signal,
          },
          "Could not load the example subject structure."
        );

        const parts = Array.isArray(structure)
          ? structure.filter((part) => part?.file)
          : [];

        if (parts.length === 0) throw new Error("This example has no published parts.");

        const playable = await loadNearestPlayablePart({
          subject,
          parts,
          requestedPartIndex,
          signal: controller.signal,
        });

        const sentenceIndex = clampIndex(
          requestedSentenceIndex,
          playable.segments.length
        );
        const segment = playable.segments[sentenceIndex];
        if (!segment) throw new Error("This example has no accessible sentence.");

        const cacheKey = [
          subjectId,
          playable.partIndex,
          sentenceIndex,
          segment.start,
          segment.end,
        ].join(":");

        const analysis = await loadNarratorAnalysis({
          subject,
          file: playable.file,
          segment,
          cacheKey,
          signal: controller.signal,
        });

        const playableAudio = await makePlayableAudioUrl(
          analysis.fragmentPath,
          controller.signal
        );

        if (!active) {
          if (playableAudio.objectUrl) URL.revokeObjectURL(playableAudio.url);
          return;
        }

        if (playableAudio.objectUrl) createdObjectUrl = playableAudio.url;

        setState({
          status: "ready",
          error: null,
          audioUrl: playableAudio.url,
          objectUrl: playableAudio.objectUrl,
          pitch: analysis.pitch,
          intensity: analysis.intensity,
          segment,
          sentenceText: segment.text || "",
          subjectTitle: subject.title || "",
          author: subject.author || "",
          partIndex: playable.partIndex,
          sentenceIndex,
          canonicalPath: `/subject/${encodeURIComponent(subjectId)}/${
            playable.partIndex
          }/${sentenceIndex}`,
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("SharedSentenceNarratorPlayer failed:", error);

        if (active) {
          setState((previous) => ({
            ...previous,
            status: "error",
            error: error?.message || "Could not load the narrator example.",
          }));
        }
      }
    };

    load();

    return () => {
      active = false;
      controller.abort();
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [requested]);

  useEffect(() => {
    const audioElements = [audioRef.current, fallbackAudioRef.current].filter(Boolean);

    for (const audio of audioElements) {
      audio.defaultPlaybackRate = playbackRate;
      audio.playbackRate = playbackRate;
      audio.preservesPitch = true;
      audio.webkitPreservesPitch = true;
    }
  }, [playbackRate, state.audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const markPlaying = () => setIsPlaying(true);
    const markStopped = () => setIsPlaying(false);

    audio.addEventListener("play", markPlaying);
    audio.addEventListener("playing", markPlaying);
    audio.addEventListener("pause", markStopped);
    audio.addEventListener("ended", markStopped);
    audio.addEventListener("emptied", markStopped);

    return () => {
      audio.removeEventListener("play", markPlaying);
      audio.removeEventListener("playing", markPlaying);
      audio.removeEventListener("pause", markStopped);
      audio.removeEventListener("ended", markStopped);
      audio.removeEventListener("emptied", markStopped);
    };
  }, [state.audioUrl]);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !state.audioUrl) return;

    if (audio.paused || audio.ended) {
      if (audio.ended) audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error("Could not play narrator example:", error);
      });
    } else {
      audio.pause();
    }
  }, [state.audioUrl]);

  const canonicalHref = useMemo(() => {
    try {
      return new URL(state.canonicalPath || href, window.location.origin).toString();
    } catch {
      return window.location.origin;
    }
  }, [href, state.canonicalPath]);

  const hasPitch = Boolean(state.pitch?.xs?.length && state.pitch?.ys?.length);

  return (
    <div
      className={[
        "rounded-2xl border border-blue-200 bg-gradient-to-br from-white to-blue-50/50 shadow-sm",
        compact ? "p-3 sm:p-4" : "p-4 sm:p-5",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className={[
              "font-semibold uppercase tracking-wide text-blue-700",
              compact ? "text-xs" : "text-sm",
            ].join(" ")}
          >
            {label}
          </div>

          {state.status === "ready" && (
            <>
              <div
                className={[
                  "mt-1 font-semibold text-gray-900",
                  compact ? "text-lg sm:text-xl" : "text-base sm:text-lg",
                ].join(" ")}
              >
                {state.sentenceText || "Narrator sentence"}
              </div>
              <div
                className={[
                  "mt-1 text-gray-500",
                  compact ? "text-xs" : "text-sm",
                ].join(" ")}
              >
                {compact ? (
                  <>
                    {state.subjectTitle}
                    {` · Part ${state.partIndex + 1}, sentence ${
                      state.sentenceIndex + 1
                    }`}
                  </>
                ) : (
                  <>
                    {state.subjectTitle}
                    {state.author ? ` · ${state.author}` : ""}
                    {` · Part ${state.partIndex + 1}, sentence ${
                      state.sentenceIndex + 1
                    }`}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <a
          href={canonicalHref}
          target="_blank"
          rel="noreferrer"
          className={[
            "inline-flex shrink-0 items-center text-sm font-semibold text-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
            compact
              ? "rounded-md px-1 py-1 underline-offset-4 hover:underline"
              : "rounded-xl border border-blue-200 bg-white px-3 py-2 hover:bg-blue-50",
          ].join(" ")}
        >
          {compact ? "Open full exercise ↗" : "Open exercise"}
        </a>
      </div>

      {state.status === "loading" && (
        <div className="mt-4 animate-pulse space-y-3" aria-live="polite">
          <div className="h-5 w-3/4 rounded bg-slate-200" />
          <div
            className={[
              "w-full rounded-xl bg-slate-200",
              compact ? "h-56" : "h-64",
            ].join(" ")}
          />
          <div className="text-sm text-gray-500">
            Preparing narrator graph and audio…
          </div>
        </div>
      )}

      {state.status === "error" && (
        <div
          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700"
          role="alert"
        >
          {state.error}
        </div>
      )}

      {state.status === "ready" && state.audioUrl && (
        <div className="mt-4 space-y-3">
          <audio
            ref={audioRef}
            src={state.audioUrl}
            preload="metadata"
            className="hidden"
          />

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={togglePlayback}
              className={[
                "inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                compact
                  ? "min-w-[8.5rem] px-3.5 py-2"
                  : "min-w-[9rem] px-4 py-2.5",
              ].join(" ")}
            >
              <span aria-hidden="true">{isPlaying ? "❚❚" : "▶"}</span>
              {isPlaying ? "Pause narrator" : "Play narrator"}
            </button>

            <fieldset className="flex items-center gap-1.5" aria-label="Playback speed">
              <legend className="sr-only">Playback speed</legend>
              <span className="mr-1 text-sm text-gray-500">Speed:</span>
              {[
                { value: 0.25, label: "25%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ].map((option) => {
                const active = playbackRate === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setPlaybackRate(option.value)}
                    className={[
                      "rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                      active
                        ? "border-blue-500 bg-blue-600 text-white shadow-sm"
                        : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700",
                    ].join(" ")}
                  >
                    {option.label}
                  </button>
                );
              })}
            </fieldset>

            <p className="basis-full text-xs leading-5 text-gray-500 sm:text-sm">
              {compact
                ? "100% natural · 50% inspect · 25% trace. Tap a word to inspect it; replay the full phrase for the contour."
                : "Click a word in the graph to replay it, then replay the full phrase to hear the contour."}
            </p>
          </div>

          {hasPitch ? (
            <PitchAnimation
              pitch={state.pitch}
              intensity={state.intensity}
              segment={state.segment}
              audioRef={audioRef}
              audioTimelineStart={0}
              audioTimeScale={1}
              fitViewport={false}
            />
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
              Narrator audio is available, but this response contains no pitch series.
              The audio remains playable below.
              <audio
                ref={fallbackAudioRef}
                className="mt-3 block w-full"
                src={state.audioUrl}
                controls
                preload="metadata"
                onLoadedMetadata={(event) => {
                  event.currentTarget.defaultPlaybackRate = playbackRate;
                  event.currentTarget.playbackRate = playbackRate;
                  event.currentTarget.preservesPitch = true;
                  event.currentTarget.webkitPreservesPitch = true;
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}